import React, { useContext, useEffect, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getEntities, getBannerByMonthData } from "../../API";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";
import MonthFormField from "../../components/MonthFormField";

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function formatMonthLabel(ym) {
  if (!ym) return "";
  const [year, month] = ym.split("-");
  return new Date(parseInt(year), parseInt(month) - 1).toLocaleString("en", { month: "long", year: "numeric" });
}

function defaultPickerMonth() {
  const now = new Date();
  return [{ month: now.getMonth() + 1, year: now.getFullYear(), monthName: now.toLocaleString("en", { month: "short" }) }];
}

function fromPickerMonth(months) {
  if (!months || months.length === 0) return "";
  const { month, year } = months[0];
  return `${year}-${String(month).padStart(2, "0")}`;
}

function computeObservations(data) {
  const products = data.products;
  const monthLabel = formatMonthLabel(data.month2);
  if (products.length === 0) return [];
  const top = products[0];
  const bottom = products[products.length - 1];
  const avg = (products.reduce((s, p) => s + p.pct2, 0) / products.length).toFixed(1);
  return [
    `Top performing banner in ${monthLabel}: ${top.name} at ${top.pct2}%.`,
    `Lowest performing banner: ${bottom.name} at ${bottom.pct2}%.`,
    `Average distribution across banners for ${monthLabel}: ${avg}%.`,
  ];
}

const BAR_COLORS = ["#4472C4", "#ED7D31", "#A5A5A5"];

export default function BannerByMonthReport() {
  const [{ manufacturer, provinces, territories, representatives }] = useContext(ReportParamsContext);

  const [pickerMonth, setPickerMonth] = useState(defaultPickerMonth);
  const month = fromPickerMonth(pickerMonth);
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const prevManufacturerId = useRef(undefined);

  useEffect(() => {
    getEntities("banners", "?size=1000").then((res) => setBanners(normalizeResponse(res)));
  }, []);

  useEffect(() => {
    const mfrId = manufacturer?.id;
    if (mfrId === prevManufacturerId.current) return;
    prevManufacturerId.current = mfrId;
    setSelectedProducts([]);
    if (!mfrId) { setProducts([]); return; }
    setProductsLoading(true);
    getEntities("products", `?search=manufacturer:${mfrId}&size=1000`)
      .then((res) => setProducts(normalizeResponse(res)))
      .finally(() => setProductsLoading(false));
  }, [manufacturer?.id]);

  const canGenerate = !!(manufacturer && month);

  const handleGenerate = () => {
    if (!canGenerate) return;
    setLoading(true);
    setData(null);
    setError(null);
    getBannerByMonthData({
      manufacturerId: manufacturer.id,
      month,
      bannerIds: selectedBanners.map((b) => b.id),
      productIds: selectedProducts.map((p) => p.id),
      provinceIds: provinces?.map((p) => p.id),
      territoryIds: territories?.map((t) => t.id),
      representativeIds: representatives?.map((r) => r.id),
    })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const observations = data ? computeObservations(data) : [];

  return (
    <div className="flex-grow-1 overflow-auto p-4">
      <h5 className="mb-4">Distribution by Banner by Month</h5>

      <div className="row g-3 mb-3 align-items-end">
        <div className="col-md-3">
          <MonthFormField
            label="Month"
            value={pickerMonth}
            onChange={setPickerMonth}
          />
        </div>
        <div className="col-md-2">
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={!canGenerate || loading}
          >
            {loading ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      <div className="row g-3 mb-4 align-items-end">
        <div className="col-md-3">
          <label className="form-label small mb-1">Banners</label>
          <DropdownMultiSelect
            options={banners}
            value={selectedBanners}
            onChange={setSelectedBanners}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            placeholder="Select banners..."
            disabled={banners.length === 0}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small mb-1">
            Products{productsLoading ? " (loading…)" : ""}
          </label>
          <DropdownMultiSelect
            options={products}
            value={selectedProducts}
            onChange={setSelectedProducts}
            getOptionLabel={(o) => [o.description, o.formatQut, o.format].filter(Boolean).join(" ")}
            getOptionValue={(o) => o.id}
            placeholder="Select products..."
            disabled={!manufacturer || productsLoading}
          />
        </div>
      </div>

      {!manufacturer && (
        <p className="text-muted">Select a manufacturer in the panel above.</p>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {data && data.products.length === 0 && (
        <p className="text-muted">No data found for the selected parameters.</p>
      )}

      {data && data.products.length > 0 && (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title text-center fs-5 mb-3">Distribution by Banner</h6>
              <ResponsiveContainer width="100%" height={380}>
                <BarChart
                  data={data.products}
                  margin={{ top: 10, right: 20, bottom: 80, left: 0 }}
                  barCategoryGap="20%"
                  barGap={2}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={36} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend verticalAlign="top" />
                  <Bar dataKey="pct0" name={formatMonthLabel(data.month0)} fill={BAR_COLORS[0]} />
                  <Bar dataKey="pct1" name={formatMonthLabel(data.month1)} fill={BAR_COLORS[1]} />
                  <Bar dataKey="pct2" name={formatMonthLabel(data.month2)} fill={BAR_COLORS[2]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <p className="fw-semibold fst-italic mb-2">Observations &amp; Opportunities:</p>
              <ul className="mb-0 small">
                {observations.map((obs, i) => <li key={i}>{obs}</li>)}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
